import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Switch } from "@shared/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { toast } from "@shared/ui/use-toast";
import {
  ArrowDown,
  ArrowUp,
  Info,
  List,
  Mail,
  MessageSquare,
  Plus,
  Search,
  X,
} from "lucide-react";
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RecipientDashboard from "./recipientDashboard";
import { useGetChoices } from "@features/ux/components/shared/hooks";
import {
  useGetAllCommunicationTemplatesByChannel,
  useGetOnlyTemplatesByEmail,
  useGetOnlyTemplatesByTextMessage,
  useGetWorkflowDetails,
  useUpdateWorkflow,
} from "@features/ux/components/workflow/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface SetupActionsStepProps {
  editMode?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  globalFormData?: any;
  updateGlobalFormData?: (data: any) => void;
}

interface Template {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  selected: boolean;
}

interface MessageSequenceItem {
  id: string;
  channel: number;
  channelName: string;
  templateId: string;
  templateName: string;
  waitDuration: number;
  waitUnit: "minutes" | "hours" | "days" | "weeks";
}

export const SetupActionsStep: React.FC<SetupActionsStepProps> = ({
  editMode = false,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  globalFormData,
  updateGlobalFormData,
}) => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  const utilityId = useRemoteUtilityId();
  const [enableSequencing, setEnableSequencing] = useState(false);
  const [emailChannelEnabled, setEmailChannelEnabled] = useState(true);
  const [smsChannelEnabled, setSmsChannelEnabled] = useState(false);
  const [emailSearchQuery, setEmailSearchQuery] = useState("");
  const [smsSearchQuery, setSmsSearchQuery] = useState("");
  const [sequenceSearchQuery, setSequenceSearchQuery] = useState("");
  const [messageSequence, setMessageSequence] = useState<MessageSequenceItem[]>(
    []
  );
  const [showAddCommunication, setShowAddCommunication] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<number>(0);
  const [selectedTemplatesForSequence, setSelectedTemplatesForSequence] =
    useState<string[]>([]);

  const { data: getChannel } = useGetChoices(
    "channel_choice",
    "quick_messaging"
  );
  const { data: templates } = useGetAllCommunicationTemplatesByChannel(
  sequenceSearchQuery,
  selectedChannel.toString(),
  showAddCommunication
);
const { data: templatesByEmail } = useGetOnlyTemplatesByEmail(emailSearchQuery);
const { data: templatesByTextMessage } = useGetOnlyTemplatesByTextMessage(smsSearchQuery);
  const { data: workflowData } = useGetWorkflowDetails(
    action === "edit" ? Number(id) : undefined
  );
  const { mutateAsync: updateWorkflow, isPending: isUpdating } =
    useUpdateWorkflow(id);

  const emailChannelKey = useMemo(() => {
    return (
      getChannel?.result?.find((item: any) => item.value === "Email")?.key ?? 0
    );
  }, [getChannel]);

  const smsChannelKey = useMemo(() => {
    return (
      getChannel?.result?.find((item: any) => item.value === "Text Message")
        ?.key ?? 1
    );
  }, [getChannel]);

  const getChannelName = (key: number) => {
    return (
      getChannel?.result?.find((item: any) => item.key === key)?.value || ""
    );
  };

  const getChannelIcon = (key: number) => {
    const name = getChannelName(key);
    if (name === "Email") return <Mail className="h-4 w-4 text-purple-600" />;
    if (name === "Text Message")
      return <MessageSquare className="h-4 w-4 text-green-600" />;
    return <MessageSquare className="h-4 w-4 text-gray-600" />;
  };

  const [emailTemplates, setEmailTemplates] = useState<Template[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<Template[]>([]);

  // Sync API templates with local state for email
  useEffect(() => {
    if (templatesByEmail?.results) {
      setEmailTemplates((prev) => {
        const newTemplates = templatesByEmail.results.map((t: any) => {
          const existingTemplate = prev.find((p) => p.id === t.id.toString());
          return {
            id: t.id.toString(),
            name: t.templateName || t.template_name,
            description: t.description || "",
            tags: [],
            selected: existingTemplate?.selected || false,
          };
        });
        return newTemplates;
      });
    }
  }, [templatesByEmail]);

  // Sync API templates with local state for SMS
  useEffect(() => {
    if (templatesByTextMessage?.results) {
      setSmsTemplates((prev) => {
        const newTemplates = templatesByTextMessage.results.map((t: any) => {
          const existingTemplate = prev.find((p) => p.id === t.id.toString());
          return {
            id: t.id.toString(),
            name: t.templateName || t.template_name,
            description: t.description || "",
            tags: [],
            selected: existingTemplate?.selected || false,
          };
        });
        return newTemplates;
      });
    }
  }, [templatesByTextMessage]);

  // Load workflow data in edit mode
  useEffect(() => {
    if (action === "edit" && workflowData?.result) {
      const result = workflowData.result;

      // Set enable sequencing from API
      if (result.enableSequencing !== undefined) {
        setEnableSequencing(result.enableSequencing);
      }

      // Load message sequence from API
      if (result.messageSequence && result.messageSequence.length > 0) {
        const loadedSequence: MessageSequenceItem[] = result.messageSequence
          .sort((a: any, b: any) => a.sequenceOrder - b.sequenceOrder)
          .map((item: any) => ({
            id: `${item.templateId}-${item.sequenceOrder}`,
            channel: item.channel,
            channelName: item.channelName,
            templateId: item.templateId,
            templateName: item.templateName,
            waitDuration: item.waitDuration,
            waitUnit: item.waitUnit,
          }));
        setMessageSequence(loadedSequence);
      }
    }
  }, [action, workflowData]);

  // Load saved data from globalFormData
  useEffect(() => {
    if (globalFormData?.communicationActions) {
      const savedData = globalFormData.communicationActions;
      if (savedData.enableSequencing !== undefined)
        setEnableSequencing(savedData.enableSequencing);
      if (savedData.emailChannelEnabled !== undefined)
        setEmailChannelEnabled(savedData.emailChannelEnabled);
      if (savedData.smsChannelEnabled !== undefined)
        setSmsChannelEnabled(savedData.smsChannelEnabled);
      if (savedData.messageSequence)
        setMessageSequence(savedData.messageSequence);
      if (savedData.selectedEmailTemplates) {
        setEmailTemplates((prev) =>
          prev.map((t) => ({
            ...t,
            selected: savedData.selectedEmailTemplates.some(
              (saved: Template) => saved.id === t.id
            ),
          }))
        );
      }
      if (savedData.selectedSmsTemplates) {
        setSmsTemplates((prev) =>
          prev.map((t) => ({
            ...t,
            selected: savedData.selectedSmsTemplates.some(
              (saved: Template) => saved.id === t.id
            ),
          }))
        );
      }
    }
  }, [globalFormData]);

  const handleTemplateToggle = (templateId: string, channelKey: number) => {
    if (!enableSequencing) {
      if (channelKey === emailChannelKey) {
        setEmailTemplates((prev) =>
          prev.map((t) =>
            t.id === templateId ? { ...t, selected: !t.selected } : t
          )
        );
      } else if (channelKey === smsChannelKey) {
        setSmsTemplates((prev) =>
          prev.map((t) =>
            t.id === templateId ? { ...t, selected: !t.selected } : t
          )
        );
      }
    }
  };

  const selectedEmailCount = emailTemplates.filter((t) => t.selected).length;
  const selectedSmsCount = smsTemplates.filter((t) => t.selected).length;

  // Build payload for step 2 (replace existing buildStep2Payload function)
  const buildStep2Payload = () => {
    if (enableSequencing) {
      // Sequencing mode payload
      const emailMessages = messageSequence.filter(
        (item) => item.channel === emailChannelKey
      );
      const smsMessages = messageSequence.filter(
        (item) => item.channel === smsChannelKey
      );

      return {
        remote_utility_id: utilityId,
        enable_sequencing: true,
        message_sequence: messageSequence.map((item, index) => ({
          sequence_order: index + 1,
          channel: item.channel,
          channel_name: item.channelName,
          template_id: item.templateId,
          template_name: item.templateName,
          wait_duration: item.waitDuration,
          wait_unit: item.waitUnit,
        })),
      };
    } else {
      // Non-sequencing mode payload
      const payload: any = {
        remote_utility_id: utilityId,
        enable_sequencing: false,
      };

      // Calculate totals for summary
      const totalEmailAvailable = emailChannelEnabled
        ? templatesByEmail?.results?.length || 0
        : 0;
      const totalSmsAvailable = smsChannelEnabled
        ? templatesByTextMessage?.results?.length || 0
        : 0;

      const selectedEmails = emailTemplates.filter((t) => t.selected);
      const selectedSms = smsTemplates.filter((t) => t.selected);
      const emailSelectedCount = emailChannelEnabled
        ? selectedEmails.length
        : 0;
      const smsSelectedCount = smsChannelEnabled ? selectedSms.length : 0;

      // Add email templates if email channel is enabled
      if (emailChannelEnabled) {
        if (selectedEmails.length > 0) {
          payload.email_templates = selectedEmails.map((t) => ({
            template_id: t.id,
            template_name: t.name,
          }));
        }
      }

      // Add SMS templates if SMS channel is enabled
      if (smsChannelEnabled) {
        if (selectedSms.length > 0) {
          payload.sms_templates = selectedSms.map((t) => ({
            template_id: t.id,
            template_name: t.name,
          }));
        }
      }

      // Add summary
      payload.summary = {
        totalUsers: totalEmailAvailable + totalSmsAvailable,
        emailOnly: emailSelectedCount,
        phoneOnly: smsSelectedCount,
        both: 0,
        noContact: 0,
      };

      return payload;
    }
  };
  // Save workflow data
  const saveWorkflowData = async () => {
    try {
      const payload = buildStep2Payload();
      await updateWorkflow(payload);
      toast({
        title: "Success",
        description: "Workflow updated successfully",
      });
      return true;
    } catch (error) {
      console.error("Failed to update workflow:", error);
      toast({
        title: "Error",
        description: "Failed to update workflow. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleAddToSequence = () => {
    setShowAddCommunication(true);
    setSelectedChannel(emailChannelKey);
    setSelectedTemplatesForSequence([]);
  };

  const handleCancelAddCommunication = () => {
    setShowAddCommunication(false);
    setSelectedChannel(emailChannelKey);
    setSelectedTemplatesForSequence([]);
    setSequenceSearchQuery("");
  };

  const handleConfirmAddCommunication = () => {
    if (templates?.results) {
      const selectedTemplateObjs = templates.results.filter((t: any) =>
        selectedTemplatesForSequence.includes(t.id.toString())
      );
      const newItems: MessageSequenceItem[] = selectedTemplateObjs.map(
        (template: any) => ({
          id: Date.now().toString() + Math.random(),
          channel: selectedChannel,
          channelName: getChannelName(selectedChannel),
          templateId: template.id.toString(),
          templateName: template.templateName || template.template_name,
          waitDuration: 0,
          waitUnit: "minutes",
        })
      );

      setMessageSequence([...messageSequence, ...newItems]);
    }
    setShowAddCommunication(false);
    setSelectedTemplatesForSequence([]);
  };

  const handleTemplateToggleForSequence = (templateId: string) => {
    const id = templateId.toString();
    setSelectedTemplatesForSequence((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleRemoveFromSequence = (id: string) => {
    setMessageSequence(messageSequence.filter((item) => item.id !== id));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newSequence = [...messageSequence];
    [newSequence[index], newSequence[index - 1]] = [
      newSequence[index - 1],
      newSequence[index],
    ];
    setMessageSequence(newSequence);
  };

  const handleMoveDown = (index: number) => {
    if (index === messageSequence.length - 1) return;
    const newSequence = [...messageSequence];
    [newSequence[index], newSequence[index + 1]] = [
      newSequence[index + 1],
      newSequence[index],
    ];
    setMessageSequence(newSequence);
  };

  const handleWaitDurationChange = (index: number, value: number) => {
    const newSequence = [...messageSequence];
    newSequence[index].waitDuration = value;
    setMessageSequence(newSequence);
  };

  const handleWaitUnitChange = (
    index: number,
    value: "minutes" | "hours" | "days" | "weeks"
  ) => {
    const newSequence = [...messageSequence];
    newSequence[index].waitUnit = value;
    setMessageSequence(newSequence);
  };

  const handleNext = async () => {
    // Call API to save before moving to next step
    const success = await saveWorkflowData();

    if (success) {
      const communicationData = {
        enableSequencing,
        emailChannelEnabled,
        smsChannelEnabled,
        messageSequence,
        selectedEmailTemplates: emailTemplates.filter((t) => t.selected),
        selectedSmsTemplates: smsTemplates.filter((t) => t.selected),
      };
      updateGlobalFormData?.({
        ...globalFormData,
        communicationActions: communicationData,
      });
      onNext?.();
      navigate(`/communication-hub/workflows/edit/${id}#review`);
    }
  };

  return (
    <div className="bg-white p-1">
      {workflowData?.result?.actionTypeDisplay !== "Event-Based Action" && (
        <RecipientDashboard data={workflowData} />
      )}
      <div className="space-y-6">
        <div>
          <PageHeader
            title="Setup Communication Actions"
            subtitle="Configure channels and templates for your workflow"
          />
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <List className="h-5 w-5 text-gray-600" />
              <div>
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                  Enable Message Sequencing{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3 w-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Send messages in a specific order with delays between
                        them
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <p className="text-xs text-gray-500">
                  Send messages in a specific order with delays between them
                </p>
              </div>
            </div>
            <Switch
              checked={enableSequencing}
              onCheckedChange={setEnableSequencing}
            />
          </div>
        </div>
        {enableSequencing && (
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
            <div className="flex items-center gap-2 mb-4">
              <List className="h-5 w-5 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-800">
                Message Sequence
              </h3>
              <span className="text-xs text-gray-500">
                {messageSequence.length} message(s) in sequence
              </span>
            </div>
            {messageSequence.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-sm text-gray-500 mb-1">
                  No messages in sequence yet
                </p>
                <p className="text-xs text-gray-400">
                  Click "Add Communication" below to start building your
                  sequence
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {messageSequence.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {/* Message Item */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="bg-blue-100 rounded p-2">
                            <span className="text-sm font-semibold text-blue-600">
                              {index + 1}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getChannelIcon(item.channel)}
                            <span className="text-xs font-medium text-gray-600">
                              {item.channelName}
                            </span>
                          </div>
                          <span className="text-sm text-gray-800">
                            {item.templateName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="h-8 w-8 p-0"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === messageSequence.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromSequence(item.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Wait Duration - Only show between messages (not after the last one) */}
                    {index < messageSequence.length - 1 && (
                      <div className="flex items-center justify-start gap-3 py-2">
                        <div className="h-8 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 6v6l4 2" />
                            </svg>
                            Wait
                          </span>
                          <Input
                            type="number"
                            min="0"
                            value={item.waitDuration}
                            onChange={(e) =>
                              handleWaitDurationChange(
                                index,
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="w-16 h-8 text-xs"
                            placeholder="0"
                          />
                          <select
                            value={item.waitUnit}
                            onChange={(e) =>
                              handleWaitUnitChange(
                                index,
                                e.target.value as
                                  | "minutes"
                                  | "hours"
                                  | "days"
                                  | "weeks"
                              )
                            }
                            className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                          >
                            <option value="minutes">minutes</option>
                            <option value="hours">hours</option>
                            <option value="days">days</option>
                            <option value="weeks">weeks</option>
                          </select>
                        </div>
                        <div className="h-8 w-px bg-gray-300"></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddToSequence}
              className="w-full mt-3 border-dashed"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Communication
            </Button>
          </div>
        )}
        <Dialog
          open={showAddCommunication}
          onOpenChange={setShowAddCommunication}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Plus className="h-5 w-5 text-blue-600" />
                Add Communication
              </DialogTitle>
              <DialogDescription>
                Select a channel and choose templates to add to your message
                sequence.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Channel
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    onClick={() => setSelectedChannel(emailChannelKey)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedChannel === emailChannelKey
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 rounded-lg p-2">
                        <Mail className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Email
                        </p>
                        <p className="text-xs text-gray-500">
                          Send professional emails
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => setSelectedChannel(smsChannelKey)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedChannel === smsChannelKey
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 rounded-lg p-2">
                        <MessageSquare className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Text Message
                        </p>
                        <p className="text-xs text-gray-500">
                          Send quick SMS messages
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select{" "}
                  {selectedChannel === emailChannelKey
                    ? "Email"
                    : "Text Message"}{" "}
                  Templates <span className="text-red-500">*</span>
                  <span className="float-right text-blue-600 text-xs">
                    {selectedTemplatesForSequence.length} selected
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Choose one or more templates. The workflow will use these
                  templates when sending messages.
                </p>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={`Search ${getChannelName(
                      selectedChannel
                    )} templates...`}
                    value={sequenceSearchQuery}
                    onChange={(e) => setSequenceSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {templates?.results && templates.results.length > 0 ? (
                    templates.results.map((template: any) => (
                      <div
                        key={template.id}
                        className={`border rounded-lg p-3 cursor-pointer transition-all ${
                          selectedTemplatesForSequence.includes(
                            template.id.toString()
                          )
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-800">
                              {template.templateName || template.template_name}
                            </h4>
                            {template.description && (
                              <p className="text-xs text-gray-500 mt-1">
                                {template.description}
                              </p>
                            )}
                            <div className="flex gap-1 mt-2">
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                                {template.channelName || template.channel_name}
                              </span>
                              {template.templateCategory && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                  {template.templateCategory.replace(/_/g, " ")}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Button as direct child for justify-between */}
                          <div className="flex items-center">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() =>
                                handleTemplateToggleForSequence(
                                  template.id.toString()
                                )
                              }
                              className="h-8 text-xs"
                            >
                              {selectedTemplatesForSequence.includes(
                                template.id.toString()
                              )
                                ? "Added"
                                : "Add to Sequence"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No templates available for this channel
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelAddCommunication}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAddCommunication}
                disabled={selectedTemplatesForSequence.length === 0}
              >
                Add to Sequence
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {!enableSequencing && (
          <>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-white p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 rounded-lg p-2">
                      <Mail className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                        Email Channel{" "}
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              Send professional emails with attachments
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </h3>
                      <p className="text-xs text-gray-500">
                        Send professional emails with attachments
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={emailChannelEnabled}
                    onCheckedChange={setEmailChannelEnabled}
                  />
                </div>
              </div>
              {emailChannelEnabled && (
                <div className="p-4 bg-gray-50">
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select Email Templates{" "}
                      <span className="text-red-500">*</span>
                      <span className="float-right text-blue-600">
                        {selectedEmailCount} selected
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Choose one or more email templates. The workflow will use
                      these templates when sending messages.
                    </p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search email templates..."
                        value={emailSearchQuery}
                        onChange={(e) => setEmailSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {templatesByEmail?.results &&
                    templatesByEmail.results.length > 0 ? (
                      templatesByEmail.results.map((template: any) => (
                        <div
                          key={template.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            emailTemplates.find(
                              (t) => t.id === template.id.toString()
                            )?.selected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                          onClick={() =>
                            handleTemplateToggle(
                              template.id.toString(),
                              emailChannelKey
                            )
                          }
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={
                                emailTemplates.find(
                                  (t) => t.id === template.id.toString()
                                )?.selected || false
                              }
                              onChange={() =>
                                handleTemplateToggle(
                                  template.id.toString(),
                                  emailChannelKey
                                )
                              }
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-800">
                                {template.templateName ||
                                  template.template_name}
                              </h4>
                              {template.description && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {template.description}
                                </p>
                              )}
                              <div className="flex gap-1 mt-2">
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                                  {template.channelName ||
                                    template.channel_name}
                                </span>
                                {template.templateCategory && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    {template.templateCategory.replace(
                                      /_/g,
                                      " "
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No email templates available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-white p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 rounded-lg p-2">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                        Text Message Channel{" "}
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Send quick text messages</p>
                          </TooltipContent>
                        </Tooltip>
                      </h3>
                      <p className="text-xs text-gray-500">
                        Send quick text messages
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={smsChannelEnabled}
                    onCheckedChange={setSmsChannelEnabled}
                  />
                </div>
              </div>
              {smsChannelEnabled && (
                <div className="p-4 bg-gray-50">
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select Text Message Templates{" "}
                      <span className="text-red-500">*</span>
                      <span className="float-right text-blue-600">
                        {selectedSmsCount} selected
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Choose one or more text message templates.
                    </p>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search text message templates..."
                        value={smsSearchQuery}
                        onChange={(e) => setSmsSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {templatesByTextMessage?.results &&
                    templatesByTextMessage.results.length > 0 ? (
                      templatesByTextMessage.results.map((template: any) => (
                        <div
                          key={template.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            smsTemplates.find(
                              (t) => t.id === template.id.toString()
                            )?.selected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                          onClick={() =>
                            handleTemplateToggle(
                              template.id.toString(),
                              smsChannelKey
                            )
                          }
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={
                                smsTemplates.find(
                                  (t) => t.id === template.id.toString()
                                )?.selected || false
                              }
                              onChange={() =>
                                handleTemplateToggle(
                                  template.id.toString(),
                                  smsChannelKey
                                )
                              }
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-800">
                                {template.templateName ||
                                  template.template_name}
                              </h4>
                              {template.description && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {template.description}
                                </p>
                              )}
                              <div className="flex gap-1 mt-2">
                                <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded">
                                  {template.channelName ||
                                    template.channel_name}
                                </span>
                                {template.templateCategory && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    {template.templateCategory.replace(
                                      /_/g,
                                      " "
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No text message templates available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={onPrevious} disabled={isFirstStep} variant="outline">
          Previous
        </Button>
        <Button onClick={handleNext} disabled={isUpdating}>
          {isUpdating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              Saving...
            </>
          ) : isLastStep ? (
            "Complete"
          ) : (
            "Continue to Next Step"
          )}
        </Button>
      </div>
    </div>
  );
};
